import { register, login, socialLogin, logout } from 'api/auth';
import { joinGroup, getAuthedUserProfile, setUserProfile } from 'api/users';
import { toast } from 'react-toastify';
import { saveLoginToken, deleteLoginToken, setApiRequestToken } from 'utils/helpers';
import { UpdateUser } from 'api/users';

const AUTHENTICATING = 'AUTHENTICATING';
const AUTH_FAILED = 'AUTH_FAILED';
const IS_AUTHED = 'IS_AUTHED';
const UPDATE_PROFILE_DATA = 'UPDATE_PROFILE_DATA';
const SET_LOGIN_STEP_TWO = 'SET_LOGIN_STEP_TWO';
const TOGGLE_SHOULD_RELOAD = 'TOGGLE_SHOULD_RELOAD';
export const LOGGING_OUT = 'LOGGING_OUT';

function authenticating() {
    return {
        type: AUTHENTICATING
    };
}

function authFailed(data) {
    toast.error(data.message);

    return {
        type: AUTH_FAILED,
        data
    };
}

export function isAuthed(data) {
    return {
        type: IS_AUTHED,
        data
    };
}

function loggingOut() {
    return {
        type: LOGGING_OUT
    };
}

export function updateProfileData(data) {
    return {
        type: UPDATE_PROFILE_DATA,
        data
    };
}

function setGroup(data) {
    return {
        type: SET_LOGIN_STEP_TWO,
        data
    };
}

export function setLoginStepTwo(data) {
    return async function(dispatch, getState) {
        try {
            const profileData = getState().authentication.profileData;
            let currentGroup = profileData.group || profileData.groups[0];

            if (data.current_group && data.current_group !== currentGroup.id) {
                const isExist = profileData.groups.some(({ id }) => id === data.current_group);
                const groupId = isExist ? data.current_group : profileData.groups[0].id;

                await UpdateUser(profileData.id, { group_id: groupId, current_group: groupId });
                ({ data: { group: currentGroup } } = await getAuthedUserProfile());
            }

            dispatch(setGroup({ ...data, group: currentGroup }));
        }
        catch (err) {
            dispatch(authFailed({ type: 'currentGroup', message: 'There was an error while loading group' }));
        }
    };
}

export function handleRegisterViaEmail(...params) {
    return async function(dispatch) {
        dispatch(authenticating());

        try {
            const { data: { token } } = await register(...params);

            saveLoginToken(token);
            setApiRequestToken();

            const { data } = await getAuthedUserProfile();

            dispatch(isAuthed(data));
        }
        catch (err) {
            let message = 'There was an error while registering';

            if (err.response && err.response.status === 404) {
                message = 'Invalid Access Key provided';
            }

            dispatch(authFailed({ type: 'register', message }));
        }
    };
}

export function handleLoginViaEmailAndJoin(...params) {
    return async function(dispatch) {
        dispatch(authenticating());

        try {
            const { data: { token } } = await login(...params);

            saveLoginToken(token);
            setApiRequestToken();

            const { data } = await getAuthedUserProfile();

        }
        catch (err) {
            let message = 'There was an error while logging in';

            if (err.response && err.response.status !== 500) {
                message = 'Email address or password is incorrect';
            }

            dispatch(authFailed({ type: 'login', message }));
            return;
        }

        try {
            await joinGroup({'access_code': params[2]});
            await logout();
            deleteLoginToken();
            dispatch(loggingOut());
            dispatch(handleLoginViaEmail(...params));
        }
        catch (err) {
            let message = 'There was an error while joining group.';
            if(err.response){
                switch (err.response.status) {
                    case 422:
                        message = 'You already joined this group. Please log in.';
                        break;
                    case 404:
                        message = 'Group code invalid or nonexistent. Try again.';
                        break;
                }
            }

            await logout();
            deleteLoginToken();
            dispatch(loggingOut());
            dispatch(authFailed({ type: 'login', message }));
        }
        
    };
}

export function handleLoginViaEmail(...params) {
    return async function(dispatch) {
        dispatch(authenticating());

        try {
            const { data: { token } } = await login(...params);

            saveLoginToken(token);
            setApiRequestToken();

            const { data } = await getAuthedUserProfile();

            dispatch(isAuthed(data));
        }
        catch (err) {
            let message = 'There was an error while logging in';

            if (err.response && err.response.status !== 500) {
                message = 'Email address or password is incorrect';
            }

            dispatch(authFailed({ type: 'login', message }));
        }
    };
}

export function toggleShouldReload(data) {
    return {
        type: TOGGLE_SHOULD_RELOAD,
        data
    };
}

export function handleLoginStepTwo(id, role = null, groupId = null, shouldReload = false) {
    return async function(dispatch) {
        try {
            const params = {};
            let setParams = false;

            if (role) {
                params['current_role'] = role;
                setParams = true;
            }

            if (groupId) {
                params['current_group'] = groupId;
                setParams = true;
            }

            if (setParams) {
                await setUserProfile(id, params);
            }

            dispatch(setLoginStepTwo(params));

            if (shouldReload) {
                dispatch(toggleShouldReload(shouldReload));
            }

            return true;
        }
        catch (err) {
            const message = 'There was an error while setting the current login role';

            dispatch(authFailed({ type: 'login', message }));

            return false;
        }
    };
}

export function handleSocialLogin(token, provider) {
    return async function(dispatch) {
        dispatch(authenticating());

        try {
            await socialLogin(token, provider);
            const { data } = await getAuthedUserProfile();

            dispatch(isAuthed(data));
        }
        catch (err) {
            let message = 'Something went wrong. Please try again';

            if (err.response && err.response.status !== 500) {
                message = 'The account you used does not have an email address. Please use another account to register/login';
            }

            dispatch(authFailed({ type: 'social', message }));
        }
    };
}

export function handleLogout() {
    return async function(dispatch) {
        await logout();
        deleteLoginToken();
        await dispatch(loggingOut());
    };
}


const initialState = {
    isLoginStepTwo: false,
    isAuthenticating: false,
    isAuthed: false,
    isAdmin: false,
    isExecutive: false,
    isCoach: false,
    authError: {},
    profileData: {},
    shouldReload: false
};

export default function authentication(state = initialState, action) {
    switch (action.type) {
        case AUTHENTICATING:
            return {
                ...state,
                isAuthenticating: true
            };

        case AUTH_FAILED:
            return {
                ...state,
                isAuthenticating: false,
                authError: action.data
            };

        case IS_AUTHED:
            return {
                ...state,
                isAuthenticating: false,
                isAuthed: true,
                authError: {},
                profileData: {
                    ...action.data,
                    available_role: JSON.parse(action.data.available_role)
                }
            };

        case UPDATE_PROFILE_DATA:
            return {
                ...state,
                profileData: {
                    ...state.profileData,
                    ...action.data,
                    available_role: JSON.parse(action.data.available_role)
                }
            };

        case SET_LOGIN_STEP_TWO: {
            const current_role = action.data.current_role || state.profileData.current_role;

            return {
                ...state,
                isLoginStepTwo: true,
                profileData: {
                    ...state.profileData,
                    ...action.data
                },
                isAdmin: current_role === 2 ? true : false,
                isExecutive: current_role === 1 ? true : false,
                isCoach: current_role === 3 ? true : false
            };
        }

        case TOGGLE_SHOULD_RELOAD: {
            return {
                ...state,
                shouldReload: action.data
            };
        }

        case LOGGING_OUT:
            return initialState;

        default:
            return state;
    }
}



// WEBPACK FOOTER //
// ./src/redux/modules/authentication.js